import type { AxiosError } from "axios";
import type {
  AuthTokens,
  BackendLoginResponse,
  BackendRefreshResponse,
  LoginPayload
} from "@repo/types";
import { tokenStorage } from "@repo/lib/auth/token-storage";
import { apiClient, setRefreshHandler } from "./base/http";

export interface RegisterPayload {
  name: string;
  email: string;
  password: string;
  phone?: string;
  businessName?: string;
  businessType?: "salon" | "spa" | "beauty_clinic" | "barbershop" | "nail_salon";
  role?: "admin" | "staff";
  referralCode?: string;
}

const mapTokens = (accessToken: string, refreshToken?: string): AuthTokens => ({
  accessToken,
  refreshToken
});

export const login = async (payload: LoginPayload) => {
  try {
    const { data } = await apiClient.post<BackendLoginResponse>("/api/auth/login", payload);
    if (!data.success) {
      throw new Error(data.message || "登入失敗，請稍後再試");
    }

    const tokens = mapTokens(data.token, data.refreshToken);
    await tokenStorage.setTokens(tokens, { remember: Boolean(payload.rememberMe) });
    return {
      user: data.user,
      tokens
    };
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string; errorType?: string }>;

    // 如果有後端返回的錯誤消息，優先使用
    if (axiosError.response?.data?.message) {
      throw new Error(axiosError.response.data.message);
    }

    // 根據 HTTP 狀態碼提供更具體的錯誤消息
    if (axiosError.response?.status === 401) {
      throw new Error("電子郵件或密碼錯誤，請重新輸入");
    } else if (axiosError.response?.status === 400) {
      throw new Error("請檢查輸入的資料是否正確");
    } else if (axiosError.response?.status === 500) {
      throw new Error("伺服器錯誤，請稍後再試");
    } else if (axiosError.code === 'ECONNREFUSED') {
      throw new Error("無法連接到伺服器，請檢查網路連線");
    } else if (axiosError.code === 'ETIMEDOUT') {
      throw new Error("連線逾時，請稍後再試");
    }

    // 最後才使用默認錯誤消息
    throw new Error("登入失敗，請稍後再試");
  }
};

export const refresh = async () => {
  const persistedTokens = tokenStorage.getTokens();
  if (!persistedTokens?.refreshToken) {
    throw new Error("缺少刷新令牌");
  }

  const response = await apiClient.post<BackendRefreshResponse>(
    "/api/auth/refresh",
    {
      refreshToken: persistedTokens.refreshToken
    },
    {
      headers: {
        Authorization: ""
      },
      // Custom flag for the interceptor
      // @ts-expect-error additional flag for internal tracking
      _retry: true
    }
  );

  const data = response.data as BackendRefreshResponse;

  if (!data.success) {
    throw new Error("刷新令牌失敗");
  }

  const updatedTokens = mapTokens(data.token, persistedTokens.refreshToken);
  await tokenStorage.setTokens(updatedTokens);
  return updatedTokens;
};

export const logout = async () => {
  try {
    await apiClient.post("/api/auth/logout");
  } finally {
    await tokenStorage.clearTokens();
  }
};

export const register = async (payload: RegisterPayload) => {
  try {
    const { data } = await apiClient.post<BackendLoginResponse>("/api/auth/register", payload);
    if (!data.success) {
      throw new Error(data.message || "註冊失敗，請稍後再試");
    }

    const tokens = mapTokens(data.token, data.refreshToken);
    await tokenStorage.setTokens(tokens, { remember: true });
    return {
      user: data.user,
      tokens
    };
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string; errorType?: string }>;

    // 如果有後端返回的錯誤消息，優先使用
    if (axiosError.response?.data?.message) {
      throw new Error(axiosError.response.data.message);
    }

    // 根據 HTTP 狀態碼提供更具體的錯誤消息
    if (axiosError.response?.status === 409) {
      throw new Error("此電子郵件已被註冊");
    } else if (axiosError.response?.status === 400) {
      throw new Error("請檢查輸入的資料是否正確");
    } else if (axiosError.response?.status === 500) {
      throw new Error("伺服器錯誤，請稍後再試");
    } else if (axiosError.code === 'ECONNREFUSED') {
      throw new Error("無法連接到伺服器，請檢查網路連線");
    } else if (axiosError.code === 'ETIMEDOUT') {
      throw new Error("連線逾時，請稍後再試");
    }

    // 最後才使用默認錯誤消息
    throw new Error("註冊失敗，請稍後再試");
  }
};

export const forgotPassword = async (email: string) => {
  const { data } = await apiClient.post<{ success: boolean; message?: string }>(
    "/api/auth/forgot-password",
    { email }
  );
  if (!data.success) {
    throw new Error(data.message || "發送重設密碼信件失敗");
  }
  return data;
};

export const resetPassword = async (payload: { token: string; password: string }) => {
  const { data } = await apiClient.post<{ success: boolean; message?: string }>(
    "/api/auth/reset-password",
    payload
  );
  if (!data.success) {
    throw new Error(data.message || "重設密碼失敗");
  }
  return data;
};

setRefreshHandler(async () => {
  await refresh();
});

export const AuthApi = {
  login,
  register,
  refresh,
  logout,
  forgotPassword,
  resetPassword,
};
